import LocalStorage from '@/library/localStorage';
import {Popup} from '@/library/popup';
import store from '@/store';
import {
    type App,
    type Component,
    type ComponentPublicInstance,
    createApp,
    defineAsyncComponent
} from 'vue';
import {type RouteLocation, type RouteLocationNamedRaw, type Router} from 'vue-router';

let _router: Router;

export function setRouter(router: Router)
{
    _router = router;
}

export function getRouter()
{
    return _router;
}

export function isEqual(a: any, b: any): boolean
{
    if (a === b)
    {
        return true;
    }
    else if (
        a && b
        && 'object' === typeof a && 'object' === typeof b
        && JSON.stringify(a) === JSON.stringify(b)
    )
    {
        return true;
    }

    return false;
}

export function toNormalizedLower(str: any): string
{
    if (null === str || 'undefined' == typeof str || 'undefined' === typeof str.toString)
    {
        return '';
    }

    let string = str.toString();

    if (string.normalize)
    {
        string = string.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    }

    return string.toLowerCase();
}

export function toKebabCase(string: string)
{
    return string.replace(/[A-Z]+(?![a-z])|[A-Z]/g, (string, split) => (split ? '-' : '') + string.toLowerCase());
}

export function compareStrings(haystack: string | number, needle: string | number): boolean
{
    return toNormalizedLower(haystack).includes(toNormalizedLower(needle));
}

export function sortableCompareStrings(a: string | number, b: string | number): number
{
    if ('string' === typeof a && 'string' === typeof b)
    {
        return a.localeCompare(b, 'de', {
            sensitivity: 'base',
            ignorePunctuation: true,
            numeric: true,
        });
    }

    if (a === b)
    {
        return 0;
    }

    return a > b ? 1 : -1;
}

export function inArray(needle: string | number, haystack: (string | number)[]): boolean
{
    needle = toNormalizedLower(needle);

    for (let value of haystack)
    {
        if (toNormalizedLower(value) == needle)
        {
            return true;
        }
    }

    return false;
}

export function arrayIntersect<T = any>(a: T[], b: T[]): T[]
{
    return a.filter((a: any) => b.includes(a));
}

export function arrayUnique<T = any>(array: T[]): T[]
{
    return array.filter((value, index, array) => array.indexOf(value) === index);
}

export function removeSurnameAffix(surname: string): string
{
    if (!surname)
    {
        return '';
    }

    return surname.replace(/^((am|der|die|das|von|vor|zu|zum) )*/ig, '');
}

export function sortPersonsByName(ids: number[], type: 'user' | 'employee' = 'user', unique: boolean = true): number[]
{
    let persons: { id: number; sort: string }[] = [];
    let check: number[]                         = [];

    for (const id of ids)
    {
        if (unique)
        {
            if (check.includes(id))
            {
                continue;
            }

            check.push(id);
        }

        const personData = store.getters.personData(id, type);
        persons.push({
            id: id,
            sort: removeSurnameAffix(personData.surname) + ' ' + personData.name,
        });
    }

    persons.sort((a, b) => sortableCompareStrings(a.sort, b.sort));

    return persons.map((person) => person.id);
}

export function isEmptyObject(o: object)
{
    let t;
    // noinspection LoopStatementThatDoesntLoopJS
    for (t in o)
    {
        if (o.hasOwnProperty(t))
        {
            return false;
        }
    }
    return true;
}

// temporary - with vue-router 3.4 (requires vue3) it is implemented @ vue-router
//+ https://router.vuejs.org/guide/advanced/navigation-failures.html#detecting-navigation-failures
export function isVueNavigationFailure(error: any)
{
    return Object.prototype.toString.call(error).indexOf('Error') > -1 && error._isRouter;
}

export function escapeRegex(regex: string)
{
    return regex.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function numberFormat(value: number | undefined, decimals = 2, grouping = false)
{
    return (value || 0).toLocaleString(
        'de-DE',
        {
            style: 'decimal',
            useGrouping: grouping,
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
        },
    );
}

export function percentFormat(value: number | undefined)
{
    return (value || 0).toLocaleString(
        'de-DE',
        {
            style: 'percent',
            useGrouping: false,
            maximumFractionDigits: 2,
        },
    );
}

export function currencyFormat(value: number | undefined)
{
    return (value || 0).toLocaleString(
        'de-DE',
        {
            style: 'currency',
            currency: 'EUR',
            useGrouping: true,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        },
    );
}

export function getFlatHierarchyArray<EntryType extends { children: EntryType[] }>(entries: EntryType[]): EntryType[]
{
    const flat: EntryType[] = [];

    function reformatHierarchy(entries: EntryType[])
    {
        for (const entry of entries)
        {
            flat.push(entry);
            if (entry.children)
            {
                reformatHierarchy(entry.children);
            }
        }
    }

    reformatHierarchy(entries);

    return flat;
}

export function NoOperation()
{
}

export function animateNumber(element: HTMLElement, endValue: number, startValue: number = 0, duration: number = 666)
{
    let range    = endValue - startValue; // no timer shorter than 50ms (not really visible any way)
    let minTimer = 50;
    let stepTime = Math.abs(Math.floor(duration / range)); // calc step time to show all interediate values
    stepTime     = Math.max(stepTime, minTimer); // never go below minTimer

    // get current time and calculate desired end time
    let startTime     = new Date().getTime();
    let endTime       = startTime + duration;
    let timer: number = 0;

    let run = () =>
    {
        let now       = new Date().getTime();
        let remaining = Math.max((endTime - now) / duration, 0);
        let value     = Math.round(endValue - (remaining * range));

        element.innerHTML = value + '';

        if (value === endValue)
        {
            clearInterval(timer);
        }
    };

    timer = window.setInterval(run, stepTime);
    run();
}

export function delay(milliseconds: number = 1000)
{
    return new Promise(resolve => setTimeout(resolve, milliseconds));
}

export function delayPromise(minDelay: number = 1_000): [Promise<any>, CallableFunction]
{
    let resolveDelay: CallableFunction = NoOperation;

    let innerDelay = new Promise(resolve =>
    {
        resolveDelay = resolve;
    });

    let delayPromise = new Promise(resolve =>
    {
        let loadStart = Date.now();

        innerDelay.then(() =>
        {
            window.setTimeout(resolve, minDelay - (Date.now() - loadStart));
        });
    });

    return [delayPromise, resolveDelay];
}

export function applyChangesPrompt(text: string = 'Möchten Sie die Änderungen anwenden oder verwerfen?', options: Partial<PopupOptions> = {}, confirmOptions: Partial<PopupButton> = {}, cancelOptions: Partial<PopupButton> = {}): Promise<void>
{
    return new Promise((resolve, reject) =>
    {
        const popup = new Popup(Object.assign({
            title: 'Daten übernehmen',
            titleIcon: 'help',
            text,
            textIcon: 'help',
            textIconClass: 'text-grey',
            size: 'small',
            buttons: [
                Object.assign({
                    title: 'Verwerfen',
                    icon: 'close',
                    type: 'secondary',
                    cmd: () =>
                    {
                        popup.close();
                        reject();
                    },
                }, cancelOptions),
                Object.assign({
                    title: 'Anwenden',
                    icon: 'check',
                    type: 'primary',
                    cmd: () =>
                    {
                        popup.close();
                        resolve();
                    },
                }, confirmOptions),
            ],
        }, options));

        popup.open();
    });
}

export function swap(array: object[], indexA: number, indexB: number)
{
    let tmp = array[indexB];

    array[indexB] = array[indexA];
    array[indexA] = tmp;
}

export function moveUp(entry: object, array: object[])
{
    const index = array.indexOf(entry);
    if (array.length <= 1 || !index)
    {
        return;
    }

    swap(array, index, index - 1);
}

export function moveDown(entry: object, array: object[])
{
    const index = array.indexOf(entry);
    if (array.length <= 1 || index >= array.length - 1)
    {
        return;
    }

    swap(array, index, index + 1);
}

export function filterObject<T extends object>(object: T, filterCallback: ({
    key,
    value,
}: { key: keyof T, value: T[keyof T] }) => boolean): T
{
    let filtered = {} as T;

    for (const key in object)
    {
        if (object.hasOwnProperty(key) && filterCallback({
            key,
            value: object[key],
        }))
        {
            filtered[key] = object[key];
        }
    }

    return filtered;
}

/**
 * Check if a given property is really an object
 * @param {any} obj the property to check
 * @return {boolean} if it's an object or not
 * @example
 * console.log(isObject(new Object())) // Should log true
 * console.log(isObject(new Array())) // Should log false
 * console.log(isObject(null)) // Should log false
 */
export function isObject(obj: any): obj is Object
{
    return obj !== null && typeof obj === 'object' && !Array.isArray(obj);
}

export function isPlainObject(value: any)
{
    return isObject(value) && [undefined, Object].includes(value.constructor as ObjectConstructor);
}

export const bettermentTax = 1.19;

export function createComponent({
    app,
    component,
    props,
    el,
}: { app: App<Element>, component: any, props: Record<string, any>, el: Element }): { app: App<Element>, component: ComponentPublicInstance }
{
    const childApp = createChildApp({
        app,
        component,
        props,
    });

    return {
        app: childApp,
        component: childApp.mount(el),
    };
}

export function createChildApp({
    app,
    component,
    props,
}: { app: App<Element>, component: any, props: Record<string, any> }): App<Element>
{
    const childApp = createApp(component, props);

    Object.assign(childApp._context, app._context);

    return childApp;
}

export function identifyElement<T extends Element = Element>(
    component: any,
): Nullable<T>
{
    return component ? (component.$el === null ? (component.root ?? null) : component.$el) : null;
}

export function isComponentName(component: Nullable<Merge<ComponentPublicInstance, { componentName?: string }>>, componentName: string): boolean
{
    return component ? (component?.$options?.name === componentName || component?.componentName === componentName) : false;
}

export function getVueChildren<T extends ComponentPublicInstance = ComponentPublicInstance>(element: Nullable<Element>): T[]
{
    if (!element)
    {
        return [];
    }

    const _children: T[] = [];
    for (const child of element.children)
    {
        if (child.__vue__)
        {
            _children.push(child.__vue__);
        }
        else if (child.children && child.children.length)
        {
            _children.push(...getVueChildren<T>(child));
        }
    }

    return _children;
}

export function pad(value: string | number, length: number, char: string | number = 0)
{
    char = char || '0';

    const pad = length - value.toString().length;
    return '' + (pad > 0 && char.toString().repeat(pad) || '') + value;
}

export function getNextArrayValue(array: any[], currentValue: any): any
{
    const index = array.indexOf(currentValue);
    return index >= 0 && index + 1 < array.length ? array[index + 1] : null;
}

export function importWrapper<T>(loader: () => Promise<T>): (errorCallback?: () => void) => Promise<T>
{
    return async (errorCallback) =>
    {
        try
        {
            return await loader();
        }
        catch (error)
        {
            try
            {
                errorCallback && errorCallback();
            }
            catch (callbackError)
            {
                // make sure reload happens in case of error
            }
            handleImportModuleError(error as Error);
            throw error;
        }
    };
}

export function defineAsyncComponentWrapper(loader: () => Promise<Component>): Component
{
    async function importCallback(): Promise<Component>
    {
        try
        {
            return await loader();
        }
        catch (error)
        {
            handleImportModuleError(error as Error);
            return Promise.reject(error);
        }
    }

    return defineAsyncComponent(() => importCallback());
}

function handleImportModuleError(error: Error): void
{
    const importModuleErrors: readonly string[] = [
        'Failed to fetch dynamically imported module',
        'error loading dynamically imported module',
    ];

    if (importModuleErrors.every(err => error.message.includes(err)))
    {
        return;
    }

    // avoid infinite reload
    let lastRefreshTime: Nullable<string> = LocalStorage.get('lastRefreshTime', null);
    const now: number                     = new Date().getTime();

    // 300000 = 5 minutes
    if (!lastRefreshTime || lastRefreshTime && ((now - parseInt(lastRefreshTime)) > 300000))
    {
        LocalStorage.set('lastRefreshTime', now.toString());
        window.location.reload();
    }
    else
    {
        throw error;
    }
}

export function redirectPreserveParams(redirectTo: RouteLocationNamedRaw, preserve: string[]): (to: RouteLocation) => RouteLocationNamedRaw
{
    return to =>
    {
        let paramsTo = to.params && filterObject(to.params, ({key}) => preserve.includes(key));

        redirectTo.params = {
            ...redirectTo.params || {},
            ...paramsTo || {},
        };

        return redirectTo;
    };
}

export function isAppleDevice()
{
    return /macintosh|mac os x|ipad|iphone|ipod/.test(navigator.userAgent.toLowerCase());
}

export function getMax<T = Nullable<number | string>>(firstValue: T, ...values: T[]): T
{
    let max: T = firstValue;

    for (const value of values)
    {
        if (max < value)
        {
            max = value;
        }
    }

    return max;
}

export function getMin<T = Nullable<number | string>>(firstValue: T, ...values: T[]): T
{
    let min: T = firstValue;

    for (const value of values)
    {
        if (value < min)
        {
            min = value;
        }
    }

    return min;
}

export default function Isset<T>(value: T | undefined | null): value is T
{
    return value !== undefined && value !== null;
}